import { Component, Host, h, Element, Event, Prop, Watch, EventEmitter } from "@stencil/core";

import { StzhSaptchaLocalizedText } from "./stzh-saptcha.localization";
import { StzhInputChangeEvent, StzhSaptchaChangeEvent } from "../../index";

let saptchaCounter = 0;

/**
 * @slot - Slot for content
 */
@Component({
  tag: "stzh-saptcha",
  styleUrl: "stzh-saptcha.scss",
  scoped: true,
})
export class StzhSaptcha {
  /** Translation strings */
  @Prop() localization: StzhSaptchaLocalizedText;

  /** Numbers shown to user for selection */
  @Prop() numbers: number[] | string = [];
  private _numbers: number[] = [];

  /** The value of the input element */
  @Prop({ mutable: true }) value: string = "";

  /** Name of input */
  @Prop({ reflect: true }) name: string = "saptcha";

  /** One or multiple error message */
  @Prop() error: string | string[];
  private _error: string[];

  /** Invalid status */
  @Prop({ reflect: true }) invalid: boolean = false;

  /** Whether the element is disabled or not */
  @Prop({ reflect: true }) disabled: boolean = false;

  @Element() element: HTMLStzhSaptchaElement;

  /** Saptcha change event */
  @Event() stzhChange: EventEmitter<StzhSaptchaChangeEvent>;

  @Watch("numbers")
  numbersWatcher(newValue: number[] | string) {
    if (typeof newValue === "string") {
      this._numbers = JSON.parse(newValue);
    } else {
      this._numbers = newValue;
    }
  }

  @Watch("error")
  errorWatcher(newValue: string | string[]) {
    if (typeof newValue === "string") {
      try {
        this._error = JSON.parse(newValue);
      } catch (e) {
        if (newValue) {
          this._error = [newValue];
        } else {
          this._error = [];
        }
      }
    } else if (newValue) {
      this._error = newValue;
    } else {
      this._error = [];
    }
  }

  private saptchaId: string;

  private onInputChange = (event: CustomEvent<StzhInputChangeEvent>) => {
    this.value = event.detail.value;

    this.stzhChange.emit({
      component: "stzh-saptcha",
      originalEvent: event,
      value: this.value,
    });
  };

  async componentWillLoad() {
    this.saptchaId = `stzh-saptcha-${saptchaCounter++}`;

    this.numbersWatcher(this.numbers);
    this.errorWatcher(this.error);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "saptcha");
    }
  }

  render() {
    const classes = {
      "stzh-saptcha": true,
    };

    return (
      <Host is-invalid={this.invalid || this._error.length > 0}>
        <div class={classes}>
          <div class="stzh-saptcha__box">
            <div class="stzh-saptcha__info" id={`${this.saptchaId}-info`}>
              {this.localization.info} {this.localization.question}
            </div>
            <div class="stzh-saptcha__answer" id={`${this.saptchaId}-answer`}>
              {this._numbers.map((number, index) => (
                <span class="stzh-saptcha__number">
                  {number}
                  {index < this._numbers.length - 1 &&
                    (index === this._numbers.length - 2 ? this.localization.answerOr : ", ")}
                </span>
              ))}
            </div>
            <stzh-input
              class="stzh-saptcha__input"
              label={this.localization.answer}
              name={this.name}
              invalid={this.invalid}
              error={this.error}
              disabled={this.disabled}
              value={this.value}
              onStzhChange={this.onInputChange}
              required
              a11yDescribedby={`${this.saptchaId}-info ${this.saptchaId}-answer`}
            ></stzh-input>
          </div>
        </div>
      </Host>
    );
  }
}
